import { useCallback, useRef, useState } from 'react'
import axios from 'axios'
import { isAxiosError } from 'shared/api/isAxiosError'
import { releaseNonArrivedMovement } from '../api/http'

function useReleaseNonArrivedMovementModal(transitOperationId: number | null) {
  const [isOpen, setIsOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isError, setIsError] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  const submit = useCallback(async (explanation: string) => {
    if (!transitOperationId) throw new Error('Missing transit operation ID')

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    try {
      setIsSubmitting(true)
      setIsError(false)
      await releaseNonArrivedMovement({
        id: transitOperationId,
        explanation,
      }, {
        signal: controller.signal,
      })

      if (!controller.signal.aborted) {
        setIsSubmitting(false)
        setIsError(false)
      }
    } catch (err) {
      if (isAxiosError(err)) {
        if (axios.isCancel?.(err) || err?.code === 'ERR_CANCELED' || controller.signal.aborted) {
          return
        }
      }
      // eslint-disable-next-line no-console
      console.error(err)
      setIsError(true)
    } finally {
      setIsSubmitting(false)
    }
  }, [transitOperationId])

  const open = useCallback(() => {
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    abortRef.current?.abort('Modal closed')
    setIsOpen(false)
    setIsError(false)
  }, [])

  return {
    submit,
    isOpen,
    open,
    close,
    isSubmitting,
    isError,
  }
}

export default useReleaseNonArrivedMovementModal
